import React from 'react'

interface SparklineProps {
  values: number[]
  width?: number
  height?: number
  /** Override the default `img`-role aria label. Pass an explicit summary
   *  like "Probability trend over 12 readings: 0.42 to 0.55" for screen
   *  readers. When omitted, a generic summary is computed from the values. */
  ariaLabel?: string
}

function formatTrendLabel(values: number[]): string {
  if (!values.length) return 'sparkline (no data)'
  const first = values[0]
  const last = values[values.length - 1]
  const dir = last > first + 0.005 ? 'up' : last < first - 0.005 ? 'down' : 'flat'
  const pct = (v: number) =>
    typeof v === 'number' && Number.isFinite(v) ? `${Math.round(v * 100)}%` : '—'
  return `Trend ${dir} across ${values.length} reading${values.length === 1 ? '' : 's'}: ${pct(first)} to ${pct(last)}`
}

export default function Sparkline({ values, width = 80, height = 28, ariaLabel }: SparklineProps) {
  if (!values || values.length === 0) return <span aria-hidden="true" />

  const pad = 3
  const w = width
  const h = height

  const label = ariaLabel || formatTrendLabel(values)

  if (values.length === 1) {
    return (
      <svg
        width={w}
        height={h}
        viewBox={`0 0 ${w} ${h}`}
        className="sparkline"
        role="img"
        aria-label={label}
      >
        <title>{label}</title>
        <circle cx={w / 2} cy={h / 2} r={2.5} fill="var(--accent)" />
      </svg>
    )
  }

  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = max - min || 1

  const points = values.map((v, i) => {
    const x = pad + (i / (values.length - 1)) * (w - pad * 2)
    const y = h - pad - ((v - min) / range) * (h - pad * 2)
    return { x, y }
  })

  const polyline = points.map((p) => `${p.x},${p.y}`).join(' ')
  const last = points[points.length - 1]

  const first = values[0]
  const latest = values[values.length - 1]
  const color = latest > first + 0.005 ? 'var(--accent)' : latest < first - 0.005 ? 'var(--bad)' : 'var(--muted)'

  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      className="sparkline"
      role="img"
      aria-label={label}
    >
      <title>{label}</title>
      <polyline
        points={polyline}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx={last.x} cy={last.y} r={2.5} fill={color} />
    </svg>
  )
}
